/**
 * `/profile` — handle, avatar, badges, aggregate stats (spec §2.6). No pixel
 * history, no map of contributions, no location list.
 */
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AvatarEditor, AvatarImage, BadgeGrid, Icon, InkGauge, TopBar } from '../components/Widgets.tsx';
import { api, ApiError } from '../lib/api.ts';
import { refreshUser } from '../lib/session.ts';
import { toast, useApp } from '../lib/store.ts';

export function Profile() {
  const user = useApp((s) => s.user);
  const authReady = useApp((s) => s.authReady);
  const params = useApp((s) => s.params);
  const [editing, setEditing] = useState(false);
  if (!authReady) return <div className="page page--paper" />;
  if (!user) return <Navigate to="/signin?next=/profile" replace />;
  return (
    <div className="page page--paper">
      <TopBar
        right={
          <Link to="/settings" className="btn btn--paper" data-testid="settings-link">
            Settings
          </Link>
        }
      />
      <div className="sheet">
        <header className="profile__head">
          <AvatarImage value={user.avatar} size={96} />
          <div>
            <h1 data-testid="handle">{user.handle}</h1>
            <div className="row" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn btn--paper"
                data-testid="reroll"
                onClick={async () => {
                  await api.rerollHandle();
                  await refreshUser();
                }}
              >
                <Icon name="spark" /> New handle
              </button>
            </div>
          </div>
        </header>
        <InkGauge ink={user.ink} wide />
        <section>
          <ul className="stats">
            <li>
              <strong data-testid="stat-pixels">{user.stats.pixelsPainted.toLocaleString()}</strong> pixels painted
            </li>
            <li>
              <strong>{user.stats.tilesVisited.toLocaleString()}</strong> places
            </li>
            <li>
              <strong>{user.badges.length}</strong> badges
            </li>
          </ul>
        </section>
        <section className="card">
          <h2>Avatar</h2>
          <p className="muted">16 × 16 from the palette, one ink per pixel. It shows on your profile only, never on the canvas.</p>
          {editing ? (
            <AvatarEditor
              value={user.avatar}
              pixelCost={params.avatar_pixel_cost}
              onSave={async (b64) => {
                try {
                  const res = await api.setAvatar(b64);
                  toast(res.completed ? 'Avatar complete. Nice.' : `Avatar saved for ${res.cost} ink.`);
                  await refreshUser();
                  setEditing(false);
                } catch (err) {
                  toast(err instanceof ApiError && err.code === 'insufficient_ink' ? 'Not enough ink for that avatar yet.' : 'Avatar did not save. Try again.');
                }
              }}
            />
          ) : (
            <div>
              <button type="button" className="btn" onClick={() => setEditing(true)}>
                <Icon name="brush" /> {user.avatar ? 'Edit avatar' : 'Draw your avatar'}
              </button>
            </div>
          )}
        </section>
        <section>
          <h2>Badges</h2>
          <BadgeGrid badges={user.badges} />
        </section>
        {user.status !== 'active' && (
          <section className="card warn">
            Account status: {user.status}
            {user.penaltyUntil ? ` until ${new Date(user.penaltyUntil).toLocaleString()}` : ''}
          </section>
        )}
      </div>
    </div>
  );
}
